import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HiMail, HiLocationMarker, HiArrowRight } from 'react-icons/hi';
import { FaGithub, FaLinkedin, FaBloggerB } from 'react-icons/fa';

const socialLinks = [
  {
    icon: FaGithub,
    label: 'GitHub',
    subLabel: 'github.com/atmanad',
    href: 'https://github.com/atmanad',
    accent: '#dce3f0',
  },
  {
    icon: FaLinkedin,
    label: 'LinkedIn',
    subLabel: 'linkedin.com/in/atmanad',
    href: 'https://www.linkedin.com/in/atmanad/',
    accent: '#0A66C2',
  },
  {
    icon: HiMail,
    label: 'Email',
    subLabel: 'atmanad0001@gmail.com',
    href: 'mailto:atmanad0001@gmail.com',
    accent: '#00f2ff',
  },
  {
    icon: FaBloggerB,
    label: 'Blog',
    subLabel: 'anewbieprogrammer.blogspot.com',
    href: 'https://anewbieprogrammer.blogspot.com/',
    accent: '#ff7b00',
  },
];

function SocialCard({ social, delay }) {
  const [hovered, setHovered] = React.useState(false);
  const Icon = social.icon;
  return (
    <motion.a
      href={social.href}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '1.25rem 1.5rem',
        background: hovered ? 'rgba(25,32,41,0.9)' : 'rgba(13,20,29,0.7)',
        border: `1px solid ${hovered ? `${social.accent}30` : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '14px',
        backdropFilter: 'blur(12px)',
        textDecoration: 'none',
        transition: 'all 0.25s ease',
        boxShadow: hovered ? `0 16px 40px rgba(0,0,0,0.4), 0 0 20px ${social.accent}08` : 'none',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: '10px',
        background: `${social.accent}10`,
        border: `1px solid ${social.accent}20`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.25s ease',
        boxShadow: hovered ? `0 0 15px ${social.accent}25` : 'none',
      }}>
        <Icon size={20} color={hovered ? social.accent : '#849495'} style={{ transition: 'color 0.2s' }} />
      </div>
      <div>
        <div style={{
          fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: '0.9rem',
          color: hovered ? '#dce3f0' : '#b9cacb', transition: 'color 0.2s',
        }}>
          {social.label}
        </div>
        <div className="text-mono" style={{ fontSize: '0.65rem', color: '#849495', marginTop: '1px' }}>
          {social.subLabel}
        </div>
      </div>
      <HiArrowRight
        size={16}
        color={hovered ? social.accent : '#3a494b'}
        style={{ marginLeft: 'auto', transition: 'all 0.2s', transform: hovered ? 'translate(3px, -3px)' : 'none' }}
      />
    </motion.a>
  );
}

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [emailHovered, setEmailHovered] = React.useState(false);

  return (
    <section id="contact" className="section-padding overflow-hidden" style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(0,242,255,0.1), transparent)',
      }} />

      {/* Big ambient glow behind section */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 70% at 50% 100%, rgba(0,242,255,0.04) 0%, transparent 70%)',
      }} />

      <div className="container-custom" ref={ref}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="section-label mb-4"
              style={{ justifyContent: 'center' }}
            >
              06 / CONTACT
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(2.5rem, 6vw, 5rem)',
                letterSpacing: '-0.04em',
                color: '#dce3f0',
                lineHeight: 1.05,
                marginBottom: '1.5rem',
              }}
            >
              Let's build something{' '}
              <span className="text-gradient">useful.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ color: '#849495', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto 2.5rem' }}
            >
              Open to opportunities, collaborations, and interesting engineering conversations.
            </motion.p>

            {/* Primary email CTA */}
            <motion.a
              href="mailto:atmanad0001@gmail.com"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              onMouseEnter={() => setEmailHovered(true)}
              onMouseLeave={() => setEmailHovered(false)}
              className="max-w-full text-xs sm:text-base px-4 sm:px-8 py-3 sm:py-4 justify-center inline-flex items-center gap-3 truncate"
              style={{
                background: emailHovered ? '#00f2ff' : 'rgba(0,242,255,0.06)',
                border: `1px solid ${emailHovered ? '#00f2ff' : 'rgba(0,242,255,0.3)'}`,
                borderRadius: '9999px',
                color: emailHovered ? '#080f17' : '#00f2ff',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                textDecoration: 'none',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                boxShadow: emailHovered ? '0 0 40px rgba(0,242,255,0.4)' : '0 0 0 rgba(0,242,255,0)',
                transform: emailHovered ? 'translateY(-3px) scale(1.02)' : 'translateY(0) scale(1)',
              }}
            >
              <HiMail size={20} />
              atmanad0001@gmail.com
              <HiArrowRight
                size={16}
                style={{
                  transition: 'transform 0.3s',
                  transform: emailHovered ? 'translate(3px, -3px)' : 'none',
                }}
              />
            </motion.a>

            {/* Location */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.45 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', marginTop: '1.25rem' }}
            >
              <HiLocationMarker size={14} color="#849495" />
              <span className="text-mono" style={{ color: '#849495', fontSize: '0.7rem', letterSpacing: '0.06em' }}>
                PUNE, INDIA
              </span>
            </motion.div>
          </div>

          {/* Social links grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
          }}>
            {socialLinks.map((social, i) => (
              <SocialCard key={social.label} social={social} delay={0.35 + i * 0.08} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
